import React from "react"
import PropTypes from "prop-types"
import { defineMessages, useIntl } from "react-intl"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import _ from "lodash"
import moment from "moment"
import path from "path"

import {
  Box,
  Button,
  IconButton,
  ThemeProvider,
  Typography
} from "@material-ui/core"
import ArchiveIcon from "@material-ui/icons/Archive"
import EditIcon from "@material-ui/icons/Edit"
import GetAppIcon from "@material-ui/icons/GetApp"
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined"
import { makeStyles } from "@material-ui/core/styles"
import {
  bindHover,
  bindPopover,
  usePopupState
} from "material-ui-popup-state/hooks"
import Popover from "material-ui-popup-state/HoverPopover"

import { tableTheme } from "../../styles"
import TooltipIconButton from "../../component/buttons/TooltipIconButton"
import { words } from "../../i18n/lstrings"
import { findModel } from "../../component/viewModelUtils"
import defaultOptions from "../../component/tables/options"
import { documentV2ArrayType, documentV2Type } from "./commonPropTypes"
import TextLabels from "../../component/tables/TextLabels"
import userFormSlice from "../../api/userFormSlice"
import documentSlice from "./documentSlice"
import useSmartFormat from "../../i18n/useSmartFormat"
import DataTable from "../../component/tables/DataTable"

const messages = defineMessages({
  fillForm: {
    id: "document.column.form.fill",
    defaultMessage: "Fill out form"
  },
  signatureRequired: {
    id: "document.column.sign.required",
    defaultMessage: "Required signature"
  },
  updatedAt: {
    id: "document.column.date",
    defaultMessage: "Publication date"
  },
  downloadZip: {
    id: "document.download_zip",
    defaultMessage: "Download ZIP archive"
  },
  publishedDate: {
    id: "document.column.published",
    defaultMessage: "Published date"
  }
})

const useStyles = makeStyles((theme) => ({
  popover: {
    pointerEvents: "none",

    "& span": {
      color: "white",
      fontSize: "11px",
      opacity: "0.9"
    }
  },
  paper: {
    padding: theme.spacing(1),
    backgroundColor: "rgba(13, 13, 13, 0.6)"
  }
}))

function DocumentInfoContent(props) {
  const format = useSmartFormat()
  const { fields, document, msgWithTransform } = props

  return _.map(fields, (field) => {
    const trLabel = format(msgWithTransform[field].message)
    const trValue = msgWithTransform[field].formatValue(format, document[field])
    return (
      <Typography key={field} variant="body2" color="textSecondary">
        <span>{`${trLabel}: `}</span>
        <span>{trValue}</span>
      </Typography>
    )
  })
}

function InfoTooltip(props) {
  const { document } = props
  const classes = useStyles()
  const infoFields = ["name"]
  const msgWithTransform = {
    name: {
      message: "Name",
      formatValue: (format, val) => val
    }
  }

  const popupState = usePopupState({
    variant: "popover",
    popupId: `tooltip-info-${document._id}`
  })

  return (
    <>
      <IconButton size="small" {...bindHover(popupState)}>
        <InfoOutlinedIcon fontSize="small" color="primary" />
      </IconButton>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        className={classes.popover}
        classes={{
          paper: classes.paper
        }}
        disableRestoreFocus
      >
        <Box>
          <DocumentInfoContent
            fields={infoFields}
            document={document}
            msgWithTransform={msgWithTransform}
          />
        </Box>
      </Popover>
    </>
  )
}
InfoTooltip.propTypes = {
  document: documentV2Type.isRequired
}

function DownloadAction(props) {
  const { document } = props
  const dispatch = useDispatch()

  const downloadOnClick = (e) => {
    e.stopPropagation()
    const action =
      document.category === "form"
        ? userFormSlice.actions.download(document)
        : documentSlice.actions.download(document)
    dispatch(action)
  }

  return (
    <TooltipIconButton
      label={words.download}
      color="primary"
      onClick={downloadOnClick}
    >
      <GetAppIcon />
    </TooltipIconButton>
  )
}
DownloadAction.propTypes = {
  document: documentV2Type.isRequired
}

function SignAction(props) {
  const { document } = props
  const history = useHistory()
  return (
    <TooltipIconButton
      label={words.sign}
      color="primary"
      onClick={(evt) => {
        evt.stopPropagation()
        history.push(`/esign/${document._id}`, document)
      }}
    >
      <EditIcon />
    </TooltipIconButton>
  )
}
SignAction.propTypes = {
  document: documentV2Type.isRequired
}

function EditAction({ document }) {
  const history = useHistory()
  const format = useSmartFormat()
  return (
    <Button
      size="small"
      color="primary"
      variant="contained"
      onClick={(evt) => {
        evt.stopPropagation()
        history.push(`/form/${document._id}`)
      }}
    >
      {format(messages.fillForm)}
    </Button>
  )
}
EditAction.propTypes = {
  document: documentV2Type.isRequired
}

export default function DocumentList(props) {
  const { model, allowZip, categoryPage } = props
  const dispatch = useDispatch()
  const intl = useIntl()
  const { formatMessage: format } = intl

  const RowActions = (value, tableMeta) => {
    const document = findModel(tableMeta, model)
    const { actions } = document
    if (!actions) {
      return null
    }

    const availableActions = []
    availableActions.push(
      <InfoTooltip key={`info-${document._id}`} document={document} />
    )
    if (actions.isEditable) {
      availableActions.push(
        <EditAction key={`edit-${document._id}`} document={document} />
      )
    }

    if (actions.isSignable) {
      availableActions.push(
        <SignAction key={`sign-${document._id}`} document={document} />
      )
    }

    if (actions.isDownloadable) {
      availableActions.push(
        <DownloadAction key={`download-${document._id}`} document={document} />
      )
    }

    if (_.isEmpty(availableActions)) {
      return null
    }

    return <Box>{availableActions}</Box>
  }

  const columns = [
    {
      // Needed as first column in order to retrieve user ID
      name: "_id",
      options: {
        display: "excluded",
        filter: false
      }
    },
    {
      // Custom quick actions column
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRender: RowActions
      }
    },
    {
      name: "name",
      label: format(words.name)
    },
    {
      name: "type",
      label: format(words.type),
      options: {
        customBodyRender: (value) => {
          if (!value || value.toLowerCase() === "null") {
            return ""
          }
          return _.capitalize(value)
        }
      }
    },
    {
      name: "signable",
      label: format(messages.signatureRequired),
      options: {
        customBodyRender: (value) => {
          if (value === true) {
            return format(words.yes)
          }

          return format(words.no)
        }
      }
    },
    {
      name: "updatedAt",
      label: format(messages.publishedDate),
      options: {
        customBodyRender: (value) => moment(value).format("L")
      }
    }
  ]
  if (categoryPage === "statements") {
    columns.splice(2, 0, {
      name: "jumpId",
      label: format(words.jumpId)
    })
    columns.splice(3, 0, {
      name: "portfolio",
      label: format(words.portfolio)
    })
  }
  const handleZipDownload = (selectedRows) => {
    const usedNames = {}
    let rows
    if (selectedRows && selectedRows.data) {
      const selectedIndexes = selectedRows.data.map(
        ({ dataIndex }) => dataIndex
      )
      rows = model.filter((ignored, index) => selectedIndexes.includes(index))
    } else {
      rows = model
    }
    const paths = rows.reduce((acc, { id, name, actions }) => {
      if (actions.isDownloadable) {
        const ext = path.extname(name)
        const basename = path.basename(name, ext)
        let filename = `${basename}${ext}`
        let uniqueIdx = 1
        while (usedNames[filename]) {
          filename = `${basename} (${uniqueIdx++})${ext}`
        }
        usedNames[filename] = true
        acc.push({ id, name: filename })
      }
      return acc
    }, [])
    dispatch(documentSlice.actions.downloadZipArchive(paths))
  }

  const options = {
    ...defaultOptions,
    textLabels: TextLabels(intl),
    filter: false,
    search: false,
    download: true,
    rowsPerPage: 50,
    rowsPerPageOptions: [10, 15, 50, 100],
    sortOrder: {
      name: "updatedAt",
      direction: "desc"
    },
    // eslint-disable-next-line react/display-name
    customToolbar: () => {
      if (!allowZip) {
        return null
      }
      return (
        <TooltipIconButton
          onClick={() => handleZipDownload()}
          label={messages.downloadZip}
        >
          <ArchiveIcon />
        </TooltipIconButton>
      )
    },
    // eslint-disable-next-line react/display-name
    customToolbarSelect: (selectedRows) => {
      if (!allowZip) {
        return null
      }
      return (
        <TooltipIconButton
          onClick={() => handleZipDownload(selectedRows)}
          label={messages.downloadZip}
        >
          <ArchiveIcon />
        </TooltipIconButton>
      )
    }
  }

  return (
    <ThemeProvider theme={tableTheme}>
      <DataTable data={model} columns={columns} options={options} />
    </ThemeProvider>
  )
}

DocumentList.propTypes = {
  model: documentV2ArrayType,
  allowZip: PropTypes.bool,
  categoryPage: PropTypes.string
}
DocumentList.defaultProps = {
  model: [],
  allowZip: false,
  categoryPage: "documents"
}
